/**
 * Wave 29 Chunk 4 — /team/announcements feed.
 *
 * Read-only for everyone; HR (owner/admin) sees a composer at the top and
 * edit/delete actions on each card.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, ChevronLeft, Plus, Loader2, Send, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import AnnouncementCard from "../components/team/AnnouncementCard";
import VoiceTextarea from "../components/VoiceTextarea";

const AUDIENCES = [
  { value: "all", label: "Everyone" },
  { value: "techs", label: "Technicians" },
  { value: "managers", label: "Managers" },
];

export default function TeamAnnouncements() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/announcements");
      setItems(data.items || []);
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditing(null); setTitle(""); setBody(""); setAudience("all"); setPinned(false); setComposeOpen(false);
  };

  const openEdit = (ann) => {
    setEditing(ann);
    setTitle(ann.title);
    setBody(ann.body);
    setAudience(ann.audience || "all");
    setPinned(!!ann.pinned);
    setComposeOpen(true);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required.");
    setSaving(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), audience, pinned };
      if (editing) {
        await api.patch(`/team/announcements/${editing.id}`, payload);
        toast.success("Announcement updated.");
      } else {
        await api.post("/team/announcements", payload);
        toast.success("Announcement posted.");
      }
      reset();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (ann) => {
    if (!window.confirm(`Delete "${ann.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/announcements/${ann.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!user) return null;
  return (
    <div className="space-y-4" data-testid="team-announcements-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team/performance" className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#1FB6C1]" /> Announcements
        </h1>
        {isHr ? (
          <button
            type="button"
            onClick={() => { reset(); setComposeOpen(true); }}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="ann-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        ) : (
          <span />
        )}
      </div>

      {composeOpen && isHr && (
        <div className="hv-card p-4 space-y-3" data-testid="ann-composer">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{editing ? "Edit announcement" : "New announcement"}</h3>
            <button onClick={reset} className="text-[#647087] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title…"
            className="hv-input text-sm w-full"
            maxLength={200}
            data-testid="ann-title"
          />
          <VoiceTextarea
            value={body}
            onChange={setBody}
            placeholder="Message…"
            rows={5}
            maxLength={4000}
            data-testid="ann-body"
           testIdPrefix="ann-body-voice" context="note" />
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <div className="flex items-center gap-1">
              <span className="text-[#647087]">Audience:</span>
              {AUDIENCES.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setAudience(a.value)}
                  className={`px-2 py-0.5 rounded-full border ${
                    audience === a.value
                      ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]"
                      : "border-white/15 text-[#a0aabf] hover:bg-white/5"
                  }`}
                  data-testid={`ann-audience-${a.value}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-1 cursor-pointer text-[#a0aabf]">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                data-testid="ann-pinned"
              />
              Pin to top
            </label>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="hv-btn hv-btn-primary text-xs ml-auto disabled:opacity-50"
              data-testid="ann-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {editing ? "Update" : "Post"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 hv-card animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">
          <Megaphone className="w-8 h-8 mx-auto mb-3 opacity-30" />
          {isHr ? "No announcements yet. Post the first one." : "No announcements yet."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="announcements-list">
          {items.map((ann) => (
            <AnnouncementCard
              key={ann.id}
              ann={ann}
              canEdit={isHr}
              onEdit={openEdit}
              onDelete={remove}
            />
          ))}
        </div>
      )}
    </div>
  );
}
